import type { ReactNode } from 'react';

import { getThemeProps, type ThemeName } from 'design-system/themes';
import type { HTMLAttributes, HTMLBlockContainerTagName } from 'types';
import { cnm } from 'utils/classNames';

import ThemeRibbon from './ThemeRibbon';

interface Props extends HTMLAttributes<HTMLElement> {
	as?: HTMLBlockContainerTagName;
	children: ReactNode;
	className?: string;
	/** If a ribbon should be shown when the theme has one. */
	showRibbon?: boolean;
	theme: ThemeName | undefined;
	/** Classes only set when there is a ribbon. */
	withRibbonClassName?: string;
}

export default function ThemedBlockContainerNew({
	as: Tag = 'div',
	children,
	className,
	showRibbon = true,
	theme,
	withRibbonClassName,
	...attrs
}: Props) {
	const themeProps = getThemeProps(theme);
	const hasRibbon = showRibbon && themeProps.hasRibbon;

	return (
		<Tag
			{...attrs}
			className={cnm(
				themeProps.baseClasses,
				// Internal ribbon classes before props to allow overrides.
				hasRibbon && 'relative py-4',
				className,
				// Custom ribbon classes after props to allow them overriding
				// the base prop classes.
				hasRibbon && withRibbonClassName,
			)}
		>
			{hasRibbon && (
				<ThemeRibbon className="absolute inset-x-0 top-0" theme={theme} />
			)}
			{children}
			{hasRibbon && (
				<ThemeRibbon className="absolute inset-x-0 bottom-0" theme={theme} />
			)}
		</Tag>
	);
}
ThemedBlockContainerNew.displayName = 'ThemedBlockContainerNew';
